import type { ICPQCart } from 'api/cart/types';

/**
 * Serializable snapshot of a configured assembly, used for card display and
 * the combined PDF. Mirrors the old AssemblyGroup ISummaryData shape; the
 * backend stores it verbatim under assemblies.summary_data.
 */
export interface ISummaryData {
  hose?: { id: string; title: string; attributes?: { image?: string; description?: string } };
  fitting1?: { id: string; title: string; attributes?: { image?: string; description?: string } };
  fitting2?: { id: string; title: string; attributes?: { image?: string; description?: string } };
  accessories?: { id: string; title: string; attributes?: { image?: string; description?: string } };
  testing?: { id: string; title: string; attributes?: { image?: string; description?: string } };
  lengthInInches?: number;
  lengthInFeet?: number;
  lengthType?: string;
  angleOfRotation?: number;
  customerPartNumber?: string;
  quantity?: number;
  notes?: string;
  generatedConfigId?: string;
  // Hydrapark and other non-hose domains carry a free-form spec list instead.
  spec?: Array<{ label: string; value: string }>;
  title?: string;
}

export type WorkspaceStatus = 'draft' | 'completing' | 'completed' | 'failed';
export type PdfStatus = 'pending' | 'rendering' | 'ready' | 'failed';
export type AccessorySource = 'catalog' | 'custom';

export interface IKitAccessory {
  uuid: string;
  partNumber: string;
  description?: string;
  category?: string;
  imageUrl?: string;
  qty: number;
  notes?: string;
  source: AccessorySource;
  position: number;
}

export interface IAssembly {
  uuid: string;
  name?: string;
  notes?: string;
  position: number;
  pdfStatus: PdfStatus;
  cartData: ICPQCart;
  summaryData: ISummaryData;
  accessories: IKitAccessory[];
}

export interface IKitLine {
  uuid: string;
  qty: number;
  position: number;
  assembly: IAssembly;
}

export interface IKit {
  uuid: string;
  name?: string;
  notes?: string;
  collapsed: boolean;
  position: number;
  lines: IKitLine[];
  accessories: IKitAccessory[];
}

export interface IWorkspaceTotals {
  kits: number;
  configs: number;
  assemblyUnits: number;
  looseUnits: number;
}

export interface IKitWorkspace {
  uuid: string;
  status: WorkspaceStatus;
  kits: IKit[];
  standalone: IAssembly[];
  totals: IWorkspaceTotals;
}

/**
 * Companions catalog product, as returned by the rules-engine companions
 * schema. Catalog fields beyond partNumber/title are dynamic and may be
 * absent (Variant A: search-only, no category chips).
 */
export interface ICompanionProduct {
  partNumber: string;
  title?: string;
  description?: string;
  imageUrl?: string;
  category?: string;
}

export interface ICompanionsPagination {
  total: number;
  perPage: number;
  page: number;
}

/**
 * Category facet for the companions catalog. `id` is the opaque value we send
 * back to filter; `name` is the attribute it filters on (e.g. ITEMTYPE);
 * `title`/`value` are the human label.
 *
 * NOTE: this id space is NOT the same as ICompanionProduct.category (which is a
 * dashed catalog UUID), so an item cannot be matched to its facet on the client.
 */
export interface ICompanionCategory {
  id: string;
  title?: string;
  value?: string;
  name?: string;
}

export interface ICompanionsSchema {
  items: ICompanionProduct[];
  pagination: ICompanionsPagination;
  // Absent when the catalog has no category facets configured.
  category?: ICompanionCategory[];
}

/** POST body of the companions schema. `category` carries selected facet ids. */
export interface ICompanionsQuery {
  search?: string;
  limit?: number;
  offset?: number;
  category?: string[];
}

export interface ISaveAssemblyPayload {
  cartData: ICPQCart;
  summaryData: ISummaryData;
  content: string;
  name?: string;
  notes?: string;
}

/**
 * The confirm-page assembly committed for a given client session, with
 * fingerprints of what was last written so a re-commit knows whether to skip,
 * patch name/notes only, or patch the full payload.
 */
export interface IPendingAssembly {
  assemblyId: string;
  dataFp: string;
  metaFp: string;
}

export interface IAddAccessoryPayload {
  partNumber: string;
  description?: string;
  category?: string;
  imageUrl?: string;
  qty: number;
  notes?: string;
  source?: AccessorySource;
}

// Backend-enforced limits, mirrored on FE for disabling controls + warnings.
export const MAX_LINES_PER_KIT = 20;
export const MIN_QTY = 1;
export const MAX_QTY = 999;
